import { useEffect, useState } from 'react';
import { WifiOff, AlertCircle } from '@vitness/icons';
import { useStore } from '../lib/store';

/**
 * Thin status line under the shell header. Silent almost always — it exists
 * for the two states that otherwise surface nowhere near where you're looking:
 * you're offline (the PWA keeps working from cache, so nothing else changes),
 * or the last sync failed. Bad network is exactly when a finance app gets
 * judged, and "quietly wrong" is the worst possible behaviour.
 */
export function StatusBanner() {
  const error = useStore(s => s.error);
  const incomeMissing = useStore(s => s.data?.incomeMissing);
  const loading = useStore(s => s.loading);
  const [online, setOnline] = useState(() =>
    typeof navigator === 'undefined' ? true : navigator.onLine);

  useEffect(() => {
    const up = () => setOnline(true);
    const down = () => setOnline(false);
    window.addEventListener('online', up);
    window.addEventListener('offline', down);
    return () => {
      window.removeEventListener('online', up);
      window.removeEventListener('offline', down);
    };
  }, []);

  if (!online) {
    return (
      <Line icon={<WifiOff size={12} />} tone="warn">
        Offline — showing your last synced data. Changes to scenarios will save when you're back.
      </Line>
    );
  }
  if (incomeMissing && !loading) {
    return (
      <Line icon={<AlertCircle size={12} />} tone="bad">
        Income synced as $0 — every percentage on the dashboard is being computed against nothing.
        This is almost always a Notion property that became a formula or a rollup; check{' '}
        <code>/api/notion/schema</code> against the names the sync reads.
      </Line>
    );
  }
  if (error && !loading) {
    return (
      <Line icon={<AlertCircle size={12} />} tone="bad">
        Sync problem: {error}
      </Line>
    );
  }
  return null;
}

function Line({ icon, tone, children }: {
  icon: React.ReactNode; tone: 'warn' | 'bad'; children: React.ReactNode;
}) {
  const c = tone === 'warn' ? 'var(--vn-warning)' : 'var(--vn-danger)';
  return (
    <div role="status" style={{
      display: 'flex', alignItems: 'center', gap: 7,
      padding: '6px 12px', marginBottom: 12,
      borderRadius: 'var(--vn-radius-md)',
      border: `1px solid color-mix(in srgb, ${c} 35%, transparent)`,
      background: `color-mix(in srgb, ${c} 8%, transparent)`,
      color: c, fontSize: 12,
    }}>
      {icon}
      <span style={{ color: 'var(--vn-text-secondary)' }}>{children}</span>
    </div>
  );
}
